/**
 * The customer flow — environment to migration. TRANSCRIBED, never authored.
 *
 * Source: `product/materials/px-onboarding-flow.html` (its `STEPS`, `LANES`,
 * `EDGES` and `TRIGGERED` constants). Every string below is VERBATIM from that
 * file: nothing is paraphrased, summarised or improved (spec I-21 / AC-6.12),
 * and `customerFlow.test.ts` asserts every countable property of the result.
 *
 * Two field-shape traps in the source, both handled here:
 *  1. `TRIGGERED` is a POSITIONAL array with NO `step` field — its entries are
 *     identified in the source only by `// 01 …` comments. `step` is derived
 *     positionally: FLOW_ACTIVATION[i].step === FLOW_STEPS[i].n.
 *  2. `LANES` uses `k:` and `cls:`, not `key:` and `side:`.
 *
 * ⚠ The six lanes are NOT roles. They are flow-time projections of the role
 * model — Client Lead ≈ `client-manager`, Client Team ⊇ `client-employee`,
 * PX Lead ≈ the manager / project-manager side, PX Admin = `admin`. `RoleKey`
 * gains nothing from this file and no `ROLE_ACCESS` row changes. A surface may
 * state the correspondence; it must never present a lane as a signed-in role.
 *
 * ⚠ This module is INERT (I-22). It is data for a rendering of a fixed
 * artefact: nothing here is dispatched, stored or mutated.
 */

export type FlowPhase = 'onboarding' | 'delivery'
export type FlowSide = 'px' | 'cl' | 'both'
export type FlowLaneKey = 'admin' | 'pxlead' | 'cllead' | 'clteam' | 'agents' | 'system'
export type FlowGroupKey = 'admin' | 'org' | 'client' | 'xc'

export interface FlowLane {
  key: FlowLaneKey
  label: string
  sub: string
  side: 'px' | 'cl'
}

/** Source `LANES` — `k` → `key`, `cls` → `side`. */
export const FLOW_LANES: readonly FlowLane[] = [
  { key: 'admin', label: 'PX Admin', sub: 'platform owner', side: 'px' },
  { key: 'pxlead', label: 'PX Lead', sub: 'engagement owner', side: 'px' },
  { key: 'cllead', label: 'Client Lead', sub: 'client sponsor', side: 'cl' },
  { key: 'clteam', label: 'Client Team', sub: 'project members', side: 'cl' },
  { key: 'agents', label: 'Agents', sub: 'expert crew', side: 'px' },
  { key: 'system', label: 'System', sub: 'what PX does', side: 'px' },
] as const

export interface FlowStep {
  /** "STEP 01" … "STEP 10". */
  n: string
  phase: FlowPhase
  title: string
  short: string
  actor: string
  side: FlowSide
  /** true for STEP 04 only — the one hard approval gate in the flow. */
  gate: boolean
  /** Per-actor activity; "—" where that lane is idle at this step. */
  lanes: Readonly<Record<FlowLaneKey, string>>
  /** Environment / project state after the step. */
  state: { env: string; proj: string }
  does: readonly string[]
  /** What the platform itself does. */
  sys: readonly string[]
  /** What now exists. */
  creates: readonly string[]
  /** The "cannot proceed until" line. */
  gateTxt: string
  watch: string
}

export const FLOW_STEPS: readonly FlowStep[] = [
  {
    n: 'STEP 01',
    phase: 'onboarding',
    title: 'Create the environment',
    short: 'Create environment',
    actor: 'PX Admin',
    side: 'px',
    gate: false,
    lanes: {
      admin: 'Provisions the tenant',
      pxlead: '—',
      cllead: '—',
      clteam: '—',
      agents: '—',
      system: 'Isolated tenant, policy applied, nothing client-visible yet',
    },
    state: { env: 'Provisioned · no users', proj: '—' },
    does: [
      'Provision a new tenant for the client',
      'Apply Perpleks org defaults and branding',
      'Set guardrails: data classification, retention, region',
      'Configure model routing and a spend cap',
      'Enable the modules this environment is allowed to use',
    ],
    sys: [
      'Creates an isolated tenant with its own data boundary',
      'Writes the creation event to the audit log',
      'Holds everything private — no client account exists yet',
    ],
    creates: [
      'Environment',
      'Policy set',
      'Module set',
      'Spend cap',
    ],
    gateTxt: 'Tenant provisioned, policy applied, spend cap set.',
    watch: 'Region and retention are painful to change once data lands. Decide both before anyone from the client logs in.',
  },
  {
    n: 'STEP 02',
    phase: 'onboarding',
    title: 'Invite the two leads',
    short: 'Invite leads',
    actor: 'PX Admin',
    side: 'px',
    gate: false,
    lanes: {
      admin: 'Invites both leads, sets scope',
      pxlead: 'Accepts, authenticates',
      cllead: 'Accepts, authenticates',
      clteam: '—',
      agents: '—',
      system: 'Enforces SSO/MFA, scopes the client lead to their own org',
    },
    state: { env: 'Active · 2 users', proj: '—' },
    does: [
      'Invite the PX Lead as engagement owner',
      'Invite the Client Lead as client sponsor',
      'Assign roles: PX Lead gets full project scope, Client Lead gets client scope only',
      'Require SSO or MFA before first login',
    ],
    sys: [
      'Sends invitations and enforces the auth policy',
      'Binds the Client Lead to a single client boundary',
      'Logs both role assignments',
    ],
    creates: [
      'PX Lead account',
      'Client Lead account',
      'Role assignments',
    ],
    gateTxt: 'Both leads have accepted and authenticated.',
    watch: 'The Client Lead is the first external person in the system. Their permission scope becomes the ceiling for everyone they add later, so get it right here.',
  },
  {
    n: 'STEP 03',
    phase: 'onboarding',
    title: 'Create and configure the project',
    short: 'Configure project',
    actor: 'PX Lead + Client Lead',
    side: 'both',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Creates project from template',
      cllead: 'Supplies scope, context and constraints',
      clteam: '—',
      agents: 'Crew selected, not yet running',
      system: 'Project in draft, work packages pre-filled, policy exceptions flagged',
    },
    state: { env: 'Active', proj: 'Draft · awaiting confirmation' },
    does: [
      'Pick an engagement template from the library',
      'Write the briefing: objective, scope, and explicitly what is out of scope',
      'Attach the blueprint: approach, phases, deliverables',
      'Set duration, milestones and work packages',
      'Set budget, rate card and the agent crew for this project',
      'Request connectors and open the client data room',
    ],
    sys: [
      'Creates the project in Draft state',
      'Pre-fills work packages and deliverables from the template',
      'Flags anything outside org policy for admin review',
    ],
    creates: [
      'Project (draft)',
      'Briefing',
      'Blueprint',
      'Work packages',
      'Budget',
      'Agent crew',
    ],
    gateTxt: 'Configuration complete and submitted for confirmation.',
    watch: 'This is the cheapest place in the whole engagement to stop scope creep. If it is not written into the briefing here, it gets negotiated later at your cost.',
  },
  {
    n: 'STEP 04',
    phase: 'onboarding',
    title: 'Admin confirms the configuration',
    short: 'Confirm config',
    actor: 'PX Admin',
    side: 'px',
    gate: true,
    lanes: {
      admin: 'Reviews and approves, or returns',
      pxlead: 'Waits or revises',
      cllead: 'Waits',
      clteam: '—',
      agents: 'Scope reviewed, still dormant',
      system: 'On approve: config version-locked. On return: change request raised',
    },
    state: { env: 'Active', proj: 'Confirmed · config locked' },
    does: [
      'Review the briefing, blueprint and duration against the signed contract',
      'Check the budget and rate card',
      'Check the agent crew scope and exactly what data it can reach',
      'Approve, or return with comments',
    ],
    sys: [
      'On approve, moves the project to Confirmed and version-locks the configuration',
      'On return, keeps the project in Draft with a change request attached',
      'Records the approval and who gave it',
    ],
    creates: [
      'Approval record',
      'Locked config version',
    ],
    gateTxt: 'Approved — or explicitly returned with comments.',
    watch: 'Make this a real gate, not a rubber stamp. It is the only point where agent data access is reviewed by someone other than the person who configured it.',
  },
  {
    n: 'STEP 05',
    phase: 'onboarding',
    title: 'Client lead onboards the team',
    short: 'Onboard team',
    actor: 'Client Lead',
    side: 'cl',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Confirms coverage per work package',
      cllead: 'Adds internal people and assigns roles',
      clteam: 'Accepts, authenticates',
      agents: '—',
      system: 'Inherits permissions from the client lead — cannot exceed them',
    },
    state: { env: 'Active', proj: 'Confirmed · staffed' },
    does: [
      'Add the internal people relevant to the project',
      'Assign each person to work packages with a role',
      'Decide who sees the data room and who only sees deliverables',
      'Nominate the client-side approver for deliverables',
    ],
    sys: [
      'Inherits every new user\'s scope from the Client Lead — never wider',
      'Notifies each person with what they can and cannot see',
      'Blocks any assignment that would exceed the client boundary',
    ],
    creates: [
      'Client team accounts',
      'Work package assignments',
      'Deliverable approver',
    ],
    gateTxt: 'Every work package has a named owner on both sides.',
    watch: 'If someone needs access wider than the Client Lead\'s scope, that is not a step 05 problem — it goes back to step 02 and a role change.',
  },
  {
    n: 'STEP 06',
    phase: 'onboarding',
    title: 'Project starts',
    short: 'Start project',
    actor: 'PX Lead + Client Lead',
    side: 'both',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Runs kickoff, enables the agent crew',
      cllead: 'Runs kickoff, confirms data sources',
      clteam: 'Joins, gets access',
      agents: 'First context index job runs',
      system: 'Project active, first index runs, cost and time tracking start',
    },
    state: { env: 'Active', proj: 'Active · Discovery' },
    does: [
      'Run kickoff against the briefing, not against a slide deck',
      'Open the data room and switch on the approved connectors',
      'Enable the agent crew inside its approved scope',
      'Start the clock: timesheets, budget tracking and agent run cost',
    ],
    sys: [
      'Moves the project to Active and sets the phase to Discovery',
      'Runs the first context indexing job across the data room',
      'Begins budget-vs-actual and run-cost tracking',
    ],
    creates: [
      'Active project',
      'First context index',
      'Open timesheets',
      'Cost tracking',
    ],
    gateTxt: 'Kickoff done, connectors healthy, first index complete.',
    watch: 'Time and cost tracking must start at this moment, not at the first invoice. There is no way to reconstruct it afterwards.',
  },
  {
    n: 'STEP 07',
    phase: 'delivery',
    title: 'Discovery',
    short: 'Discovery',
    actor: 'PX Lead + Client Team',
    side: 'both',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Runs intake, validates what the agents extracted',
      cllead: 'Opens source systems, arranges the interviews',
      clteam: 'Answers, supplies documents and data',
      agents: 'Info agent parses documents, Data agent profiles sources, context graph built',
      system: 'Indexes the data room, reports coverage gaps, costs every run',
    },
    state: { env: 'Active', proj: 'Active · Discovery' },
    does: [
      'Run intake interviews against the discovery questions in the briefing',
      'Point the agents at the client\'s source systems and documents',
      'Review what the agents extracted and correct what is wrong',
      'Agree the as-is baseline with the client in writing',
    ],
    sys: [
      'Builds and indexes the client context graph',
      'Profiles each source and reports where coverage is thin',
      'Logs every agent run with its token and euro cost',
    ],
    creates: [
      'Context graph',
      'As-is baseline',
      'Source inventory',
      'Coverage report',
    ],
    gateTxt: 'Context graph indexed, as-is baseline agreed and signed off.',
    watch: 'Agent output reads as confident whether or not the context is complete. The coverage report is the only honest signal — read it before you trust the findings.',
  },
  {
    n: 'STEP 08',
    phase: 'delivery',
    title: 'Design',
    short: 'Design',
    actor: 'PX Lead + Client Lead',
    side: 'both',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Shapes and costs the options, presents them',
      cllead: 'Chooses an option, signs off the design',
      clteam: 'Reviews the design against their reality',
      agents: 'Tech agent drafts options, Info agent pulls precedent from the knowledge base',
      system: 'Drafts from templates, versions every deliverable, flags budget breaches',
    },
    state: { env: 'Active', proj: 'Active · Design' },
    does: [
      'Draft solution options against the blueprint',
      'Cost each option using the rate card and the expected agent run cost',
      'Put every draft through human review before the client sees it',
      'Get written approval on the chosen option',
    ],
    sys: [
      'Drafts from the template library rather than a blank page',
      'Versions every deliverable: draft, review, approved',
      'Flags when the design pushes past the approved budget',
    ],
    creates: [
      'Design options',
      'Chosen design',
      'Cost model',
      'Approved deliverable v1',
    ],
    gateTxt: 'Design approved, options costed, budget still within tolerance.',
    watch: 'Last cheap moment to change direction. After Build starts, every change costs migration work as well as build work.',
  },
  {
    n: 'STEP 09',
    phase: 'delivery',
    title: 'Build',
    short: 'Build',
    actor: 'PX Lead + Client Team',
    side: 'both',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Runs delivery, watches the burn weekly',
      cllead: 'Reviews increments, keeps the business aligned',
      clteam: 'Tests increments and feeds back',
      agents: 'Tech and Data agents produce, tool calls into client systems, every run costed',
      system: 'Tracks budget vs actual live, refuses runs that would breach the spend cap',
    },
    state: { env: 'Active', proj: 'Active · Build' },
    does: [
      'Produce against the work packages',
      'Review every agent-produced artifact before it counts as done',
      'Watch budget vs actual weekly, not at the end',
      'Keep the client testing increments as they land',
    ],
    sys: [
      'Executes agent runs inside their approved scope only',
      'Meters token and euro cost per run against the project budget',
      'Refuses any run that would breach the spend cap',
    ],
    creates: [
      'Built increments',
      'Accepted work packages',
      'Run cost record',
    ],
    gateTxt: 'Work packages complete, increments accepted, spend inside the run budget.',
    watch: 'This is where agent cost quietly eats the margin. If Run Log & Cost is not live before this phase, you find out at invoice — and by then it is a write-off.',
  },
  {
    n: 'STEP 10',
    phase: 'delivery',
    title: 'Migrate',
    short: 'Migrate',
    actor: 'PX Lead + Client Lead',
    side: 'both',
    gate: false,
    lanes: {
      admin: '—',
      pxlead: 'Plans and runs the cutover, owns the rollback path',
      cllead: 'Owns the go / no-go and the business sign-off',
      clteam: 'Validates migrated data, runs acceptance checks',
      agents: 'Data agents reconcile source against target and flag mismatches',
      system: 'Records the reconciliation, holds the rollback open, logs everything',
    },
    state: { env: 'Active', proj: 'Active · Migrate' },
    does: [
      'Plan the cutover with a named, tested rollback path',
      'Migrate in a rehearsal first, then for real',
      'Reconcile source against target and resolve every mismatch',
      'Take a written go / no-go from the client, not a verbal one',
    ],
    sys: [
      'Reconciles record counts and values and flags the differences',
      'Keeps an audit trail of what moved, when, and by whom',
      'Holds the rollback path open until acceptance is signed',
    ],
    creates: [
      'Migration plan',
      'Reconciliation report',
      'Acceptance sign-off',
      'Rollback path',
    ],
    gateTxt: 'Data reconciled, acceptance checks passed, rollback tested, go / no-go given.',
    watch: 'Agents are good at spotting mismatches and bad at deciding what they mean. Every flagged difference needs a named human owner before cutover.',
  },
] as const

export interface FlowEdge {
  n: 1 | 2 | 3 | 4 | 5 | 6
  from: string
  to: string
  txt: string
  /** Edges 4 and 6 are the RETURN (evidence) edges — the loop closing. */
  ret: boolean
}

export const FLOW_EDGES: readonly FlowEdge[] = [
  { n: 1, from: 'Admin', to: 'Organization', txt: 'capabilities · policy · entitlements', ret: false },
  { n: 2, from: 'Organization', to: 'Admin', txt: 'config · module choices', ret: false },
  { n: 3, from: 'Organization', to: 'Client', txt: 'contract · team · rate card', ret: false },
  { n: 4, from: 'Client', to: 'Organization', txt: 'hours · margin · client P&L', ret: true },
  { n: 5, from: 'Admin', to: 'Client', txt: 'agent runs · tools · guardrails', ret: false },
  { n: 6, from: 'Client', to: 'Admin', txt: 'run logs · eval scores · cost', ret: true },
] as const

export interface FlowActivation {
  /** Derived POSITIONALLY from the source's unlabelled `TRIGGERED` array. */
  step: string
  admin: readonly string[]
  org: readonly string[]
  client: readonly string[]
  xc: readonly string[]
  /** Which of the six loop edges are live at this step. */
  edges: readonly number[]
  note?: string
}

export const FLOW_ACTIVATION: readonly FlowActivation[] = [
  {
    step: 'STEP 01',
    admin: [
      'Tenant Provisioning',
      'Access Manager',
      'Model Gateway',
      'Guardrails & Policy',
      'Agent & Module Registry',
    ],
    org: [
      'Organization Profile',
    ],
    client: [],
    xc: [
      'Retention & compliance',
    ],
    edges: [1, 2],
    note: 'Platform-only. Nothing in the Client block can fire because no client record exists yet.',
  },
  {
    step: 'STEP 02',
    admin: [
      'Access Manager',
      'Audit Log',
    ],
    org: [
      'People & Roles (HR)',
      'Client Portfolio',
    ],
    client: [
      'Client Profile',
    ],
    xc: [
      'Identity & permission inheritance',
      'Notifications & inbox',
    ],
    edges: [1, 3],
    note: 'First time the Client block is touched. Permission inheritance is set here and constrains every later step.',
  },
  {
    step: 'STEP 03',
    admin: [
      'Template Library',
      'Agent & Module Registry',
      'Guardrails & Policy',
    ],
    org: [
      'Contracts',
      'Budget',
      'Rates & Capacity',
      'Firm Knowledge Base',
    ],
    client: [
      'Project Workspace',
      'Work Packages',
      'Client Data Room',
      'Access & Sharing',
      'Project Agent Crew',
    ],
    xc: [],
    edges: [1, 2, 3],
    note: 'The widest step. All three blocks are active at once — which is exactly why it needs a confirmation gate after it.',
  },
  {
    step: 'STEP 04',
    admin: [
      'Guardrails & Policy',
      'Audit Log',
      'Agent & Module Registry',
      'Access Manager',
    ],
    org: [
      'Contracts',
      'Budget',
    ],
    client: [
      'Project Agent Crew',
      'Access & Sharing',
    ],
    xc: [],
    edges: [1, 2],
    note: 'Read-and-approve only. Nothing new is created except the approval record and the locked config version.',
  },
  {
    step: 'STEP 05',
    admin: [
      'Access Manager',
    ],
    org: [],
    client: [
      'Client Profile',
      'Access & Sharing',
      'Work Packages',
      'Consultant Assignment',
    ],
    xc: [
      'Identity & permission inheritance',
      'Notifications & inbox',
    ],
    edges: [3],
    note: 'Client-side only. The Organization block is idle — Perpleks does not touch this step at all if self-service is enabled.',
  },
  {
    step: 'STEP 06',
    admin: [
      'Triggers & Scheduler',
      'Orchestrator',
      'Tools & Connectors',
      'Expert Agents',
      'Run Log & Cost',
      'Metering & Billing',
      'Audit Log',
    ],
    org: [
      'Rates & Capacity',
    ],
    client: [
      'Project Workspace',
      'Client Data Room',
      'Client Context Graph',
      'Hours / Timesheets',
      'Budget vs Actual',
      'Project Agent Crew',
    ],
    xc: [
      'Analytics & dashboards',
      'Notifications & inbox',
    ],
    edges: [3, 5, 4, 6],
    note: 'The runtime wakes up. Four edges fire at once for the first time — including both return edges, which is what makes the loop closed.',
  },
  {
    step: 'STEP 07',
    admin: [
      'Triggers & Scheduler',
      'Orchestrator',
      'Expert Agents',
      'Tools & Connectors',
      'Results Store',
      'Run Log & Cost',
      'Audit Log',
    ],
    org: [
      'Firm Knowledge Base',
    ],
    client: [
      'Client Data Room',
      'Client Context Graph',
      'Work Packages',
      'Hours / Timesheets',
      'Project Agent Crew',
    ],
    xc: [
      'Search',
      'Notifications & inbox',
    ],
    edges: [3, 4, 5, 6],
    note: 'Peak runtime load. The Admin block is doing most of the work and the client is mostly supplying access.',
  },
  {
    step: 'STEP 08',
    admin: [
      'Template Library',
      'Expert Agents',
      'Orchestrator',
      'Human Review Gate',
      'Run Log & Cost',
    ],
    org: [
      'Firm Knowledge Base',
      'Budget',
      'Rates & Capacity',
    ],
    client: [
      'Project Workspace',
      'Deliverables',
      'Deliverable Versioning',
      'Budget vs Actual',
      'Hours / Timesheets',
    ],
    xc: [
      'Notifications & inbox',
    ],
    edges: [3, 4, 5, 6],
    note: 'First step where the review gate and deliverable versioning really matter — this is the first thing the client signs.',
  },
  {
    step: 'STEP 09',
    admin: [
      'Expert Agents',
      'Orchestrator',
      'Tools & Connectors',
      'Results Store',
      'Human Review Gate',
      'Run Log & Cost',
      'Metering & Billing',
      'Audit Log',
    ],
    org: [
      'Budget',
      'Rates & Capacity',
    ],
    client: [
      'Work Packages',
      'Deliverables',
      'Deliverable Versioning',
      'Hours / Timesheets',
      'Budget vs Actual',
      'Margin & Utilization',
      'Project Agent Crew',
    ],
    xc: [
      'Analytics & dashboards',
      'Notifications & inbox',
    ],
    edges: [3, 4, 5, 6],
    note: 'The widest step in the whole flow. Every block is loaded and both return edges are carrying real money.',
  },
  {
    step: 'STEP 10',
    admin: [
      'Expert Agents',
      'Orchestrator',
      'Tools & Connectors',
      'Human Review Gate',
      'Guardrails & Policy',
      'Run Log & Cost',
      'Audit Log',
    ],
    org: [
      'Contracts',
    ],
    client: [
      'Client Data Room',
      'Work Packages',
      'Deliverables',
      'Deliverable Versioning',
      'Hours / Timesheets',
      'Budget vs Actual',
    ],
    xc: [
      'Retention & compliance',
      'Notifications & inbox',
    ],
    edges: [3, 4, 5, 6],
    note: 'Guardrails come back on. Moving real client data is the highest-risk thing the platform does before go-live.',
  },
] as const

/**
 * Where the flow stops. The tail beyond migration is the run contract, not the
 * project — the same out-of-scope treatment Rev A sheet 3 gets. `why` is the
 * source's own line, verbatim.
 */
export const FLOW_SCOPE_TAIL = {
  label: 'Go-live · run',
  inScope: false,
  why: 'Go-live, run and evolve. The flow stops once migration is accepted — after that it is the run contract, not the project.',
} as const

export const flowStepByN = (n: string): FlowStep => {
  const step = FLOW_STEPS.find((s) => s.n === n)
  if (!step) throw new Error(`Unknown flow step: ${n}`)
  return step
}

export const activationFor = (n: string): FlowActivation => {
  const activation = FLOW_ACTIVATION.find((a) => a.step === n)
  if (!activation) throw new Error(`No activation for flow step: ${n}`)
  return activation
}

export const flowEdgeByN = (n: number): FlowEdge => {
  const edge = FLOW_EDGES.find((e) => e.n === n)
  if (!edge) throw new Error(`Unknown flow edge: ${n}`)
  return edge
}

/** Total item count fired at a step — the "widest activation" measure. */
export const activationWidth = (a: FlowActivation): number =>
  a.admin.length + a.org.length + a.client.length + a.xc.length
